import { useEffect, useState, type FormEvent, type MouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { Lock, X } from '../icons'
import { api, ApiError } from '../lib/api'

interface Props {
  deviceName: string
  /** Italian phrasing for what the PIN authorizes: 'toggle' or 'schedule'. */
  intent: 'toggle' | 'schedule'
  /**
   * Optional summary shown AFTER successful PIN verify (toggle intent only).
   * Lets the user review and explicitly confirm the action — safer for a
   * live restaurant fleet and easier to test the PIN flow without firing.
   * If omitted (e.g. for 'schedule'), the modal calls onConfirmed immediately.
   */
  toggleSummary?: { fromLabel: string; toLabel: string }
  asSheet: boolean
  onClose: () => void
  /** Called after the PIN has been confirmed AND (for toggle) the user clicked the final confirm. */
  onConfirmed: () => void
}

type Mode = 'verify' | 'change' | 'confirm'

const INTENT_COPY: Record<Props['intent'], string> = {
  toggle: 'Per modificare lo stato di questo dispositivo serve il PIN amministratore.',
  schedule: 'Per gestire la programmazione di questo dispositivo serve il PIN amministratore.',
}

export function PinPromptModal({ deviceName, intent, toggleSummary, asSheet, onClose, onConfirmed }: Props) {
  const [mode, setMode] = useState<Mode>('verify')

  // Verify-mode state
  const [pin, setPin] = useState('')

  // Change-mode state
  const [currentPin, setCurrentPin] = useState('')
  const [newPin, setNewPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const onBackdrop = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const goToChange = () => {
    setMode('change')
    setError(null)
    setSuccessMsg(null)
    setCurrentPin('')
    setNewPin('')
    setConfirmPin('')
  }
  const goToVerify = () => {
    setMode('verify')
    setError(null)
    setSuccessMsg(null)
    setPin('')
  }

  const onDigit = (
    val: string,
    setter: (v: string) => void,
  ) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 4)
    setter(cleaned)
    setError(null)
  }

  const submitVerify = async (e: FormEvent) => {
    e.preventDefault()
    if (pin.length !== 4) return
    setSubmitting(true)
    setError(null)
    try {
      await api.verifyPin(pin)
      // For toggle with a summary: pause on an explicit confirm step so the
      // user can review (or back out) before anything actually fires.
      // For schedule (or toggle without summary): proceed immediately.
      if (intent === 'toggle' && toggleSummary) {
        setMode('confirm')
        setSubmitting(false)
        return
      }
      onConfirmed()
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('PIN errato. Riprova.')
      } else {
        setError((err as Error)?.message || 'Errore durante la verifica')
      }
      setSubmitting(false)
    }
  }

  const submitConfirm = (e: FormEvent) => {
    e.preventDefault()
    onConfirmed()
  }

  const submitChange = async (e: FormEvent) => {
    e.preventDefault()
    if (currentPin.length !== 4 || newPin.length !== 4 || confirmPin.length !== 4) return
    if (newPin !== confirmPin) {
      setError('I due nuovi PIN non coincidono')
      return
    }
    if (newPin === currentPin) {
      setError('Il nuovo PIN deve essere diverso da quello attuale')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await api.changePin({ currentPin, newPin })
      setSuccessMsg('PIN aggiornato. Inserisci il nuovo PIN per continuare.')
      setMode('verify')
      setCurrentPin('')
      setNewPin('')
      setConfirmPin('')
      setPin('')
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError((err as Error)?.message || 'Errore durante il cambio PIN')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return createPortal(
    <div
      className={`modal-overlay${asSheet ? ' modal-overlay--sheet' : ''}`}
      style={{ zIndex: 80 }}
      onClick={onBackdrop}
    >
      <form
        className={`modal modal--narrow${asSheet ? ' modal--sheet' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pin-modal-title"
        onSubmit={
          mode === 'verify' ? submitVerify
          : mode === 'change' ? submitChange
          : submitConfirm
        }
      >
        {asSheet && <div className="modal__grabber" aria-hidden="true" />}
        <div className="modal__header">
          <div className="modal__title-wrap">
            <div className="modal__icon"><Lock /></div>
            <h2 className="modal__title" id="pin-modal-title">
              {mode === 'verify' ? (
                <>Conferma con PIN <span className="dim">— {deviceName}</span></>
              ) : mode === 'change' ? (
                <>Cambia PIN amministratore</>
              ) : (
                <>Conferma operazione <span className="dim">— {deviceName}</span></>
              )}
            </h2>
          </div>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Chiudi">
            <X />
          </button>
        </div>

        <div className="modal__body">
          {mode === 'confirm' && toggleSummary ? (
            <>
              <p className="modal__helper" style={{ marginTop: 0 }}>
                Stai per cambiare lo stato di <strong>{deviceName}</strong>. Verifica e conferma per procedere.
              </p>
              <div className="pin-confirm">
                <div className="pin-confirm__row">
                  <span className="pin-confirm__k">Da</span>
                  <span className="pin-confirm__v pin-confirm__v--from">{toggleSummary.fromLabel}</span>
                </div>
                <div className="pin-confirm__arrow">→</div>
                <div className="pin-confirm__row">
                  <span className="pin-confirm__k">A</span>
                  <span className="pin-confirm__v pin-confirm__v--to">{toggleSummary.toLabel}</span>
                </div>
              </div>
              <p className="modal__helper" style={{ marginBottom: 0 }}>
                Per annullare senza alcun effetto premi <strong>Annulla</strong>.
              </p>
            </>
          ) : mode === 'verify' ? (
            <>
              <p className="modal__helper" style={{ marginTop: 0 }}>{INTENT_COPY[intent]}</p>
              {successMsg && <div className="modal__success" role="status">{successMsg}</div>}
              <div className="field">
                <label htmlFor="pin-input">PIN (4 cifre)</label>
                <input
                  id="pin-input"
                  className="pin-input"
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  pattern="\d{4}"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => onDigit(e.target.value, setPin)}
                  disabled={submitting}
                />
              </div>
              {error && <div className="modal__error" role="alert">{error}</div>}
              <button
                type="button"
                className="modal__linkbtn"
                onClick={goToChange}
                disabled={submitting}
              >
                Cambia PIN…
              </button>
            </>
          ) : (
            <>
              <p className="modal__helper" style={{ marginTop: 0 }}>
                Inserisci il PIN attuale, poi il nuovo PIN (4 cifre) due volte per conferma.
              </p>
              <div className="field">
                <label htmlFor="pin-current">PIN attuale</label>
                <input
                  id="pin-current"
                  className="pin-input"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  autoFocus
                  maxLength={4}
                  value={currentPin}
                  onChange={(e) => onDigit(e.target.value, setCurrentPin)}
                  disabled={submitting}
                />
              </div>
              <div className="field">
                <label htmlFor="pin-new">Nuovo PIN</label>
                <input
                  id="pin-new"
                  className="pin-input"
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  maxLength={4}
                  value={newPin}
                  onChange={(e) => onDigit(e.target.value, setNewPin)}
                  disabled={submitting}
                />
              </div>
              <div className="field">
                <label htmlFor="pin-confirm">Conferma nuovo PIN</label>
                <input
                  id="pin-confirm"
                  className="pin-input"
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  maxLength={4}
                  value={confirmPin}
                  onChange={(e) => onDigit(e.target.value, setConfirmPin)}
                  disabled={submitting}
                />
              </div>
              {error && <div className="modal__error" role="alert">{error}</div>}
              <button
                type="button"
                className="modal__linkbtn"
                onClick={goToVerify}
                disabled={submitting}
              >
                ← Torna all'inserimento PIN
              </button>
            </>
          )}
        </div>

        <div className="modal__footer">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={submitting}>
            Annulla
          </button>
          {mode === 'verify' ? (
            <button type="submit" className="btn btn--primary" disabled={pin.length !== 4 || submitting}>
              {submitting ? 'Verifica…' : 'Conferma'}
            </button>
          ) : mode === 'change' ? (
            <button
              type="submit"
              className="btn btn--primary"
              disabled={currentPin.length !== 4 || newPin.length !== 4 || confirmPin.length !== 4 || submitting}
            >
              {submitting ? 'Salvataggio…' : 'Salva nuovo PIN'}
            </button>
          ) : (
            <button type="submit" className="btn btn--primary">
              {`Sì, ${toggleSummary?.toLabel.toLowerCase() ?? 'procedi'}`}
            </button>
          )}
        </div>
      </form>
    </div>,
    document.body,
  )
}
